import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';

import { getTreeNoValidDataSourceError } from '@angular/cdk/tree';

import { ActivatedRoute, Router } from '@angular/router';
import { GameDetailsViewModel, GameModel } from '../../../shared/models/GameModels';

import { MaterialModule } from '../../../material/material.module';
import { GameHeaderFullComponent } from '../../components/game-header-full/game-header-full.component';
import { GameHeaderSlimComponent } from '../../components/game-header-slim/game-header-slim.component';
import { TeamRosterComponent } from '../../components/team-roster/team-roster.component';
import { GameLineupComponent } from '../../components/game-lineup/game-lineup.component';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GameService } from '../../../shared/services/game.service';
import { GameStatisticsService } from '../../../shared/services/game-statistics.service';
import { catchError } from 'rxjs';
import { ScoutingFlowService } from '../../../shared/services/scouting-flow.service';
import { FlowItemModel } from '../../../shared/models/FlowItemModel';
import { ReportCardComponent } from '../../../scouting/components/report-card/report-card.component';
import { NoteCardComponent } from '../../../scouting/components/note-card/note-card.component';
import { MatDialogModule } from '@angular/material/dialog';
import { ScheduleService } from '../../../shared/services/schedule.service';
import { ScheduleEvent } from '../../../shared/models/ScheduleModels';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';


@Component({
    selector: 'app-game-details',
  imports: [CommonModule, MaterialModule, MatDialogModule, GameHeaderFullComponent, GameHeaderSlimComponent,
     TeamRosterComponent, GameLineupComponent, TranslateModule, ReportCardComponent, NoteCardComponent,
    TranslatePipe
  ],
    templateUrl: './game-details.component.html',
    styleUrls: ['./game-details.component.css']
})
export class GameDetailsComponent implements OnInit {

  _gameData: GameDetailsViewModel | null = null;
  _gameId: string;
  TeamHomeName: string="";
  TeamAwayName: string="";
  isBusy: boolean = false;
  isLoading: boolean = false;
  loadError: string | null = null;

  scoutingService = inject(ScoutingFlowService);
  _flowitems: FlowItemModel[] = [];
  gameCalendarEvents: ScheduleEvent[] = [];
  scoutsForGame: { userId: string; displayName: string; comment: string }[] = [];
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  headerCollapsed = signal(true);

  constructor(public _gameService: GameService,
    public route: ActivatedRoute,
    public _router: Router,
    public location: Location,
    public _statsService: GameStatisticsService,
    private scheduleService: ScheduleService) {
   }

  ngOnInit() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const gameId = params.get('GameId') ?? this.resolveGameIdFromRoute();
        if (!gameId) {
          this._gameData = null;
          this._flowitems = [];
          this.loadError = 'Missing GameId';
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        if (gameId === this._gameId && this._gameData) {
          return;
        }

        this._gameId = gameId;
        this.loadError = null;
        this.getGameData(this._gameId);
        this.getReports(this._gameId);
      });
  }

  private resolveGameIdFromRoute(): string {
    let current: ActivatedRoute | null = this.route;

    while (current) {
      const fromParams = current.snapshot.paramMap.get('GameId');
      if (fromParams) {
        return fromParams;
      }

      const fromQuery = current.snapshot.queryParamMap.get('GameId');
      if (fromQuery) {
        return fromQuery;
      }

      current = current.parent;
    }

    return '';
  }


  toggleHeaderCollapsed() {
    this.headerCollapsed.set(!this.headerCollapsed());
  }

  back(){
    this.location.back();
  }

  getReports(GameId: string) {
    this.scoutingService.getAllByGame(GameId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this._flowitems = data || [];
          this.cdr.detectChanges();
        },
        error: () => {
          this._flowitems = [];
          this.cdr.detectChanges();
        }
      });
  }

  getGameData(GameId:string){
    this.isLoading = true;
    this.loadError = null;
    this._gameService.getGameDetails(GameId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (data) => {
          const homeTeam = data?.HomeTeam ?? { Players: [], LineUpInfo: [] };
          const awayTeam = data?.AwayTeam ?? { Players: [], LineUpInfo: [] };

          this._gameData = {
            ...data,
            Game: data?.Game,
            Division: data?.Division,
            HomeTeam: {
              ...homeTeam,
              Players: homeTeam?.Players ?? [],
              LineUpInfo: homeTeam?.LineUpInfo ?? []
            },
            AwayTeam: {
              ...awayTeam,
              Players: awayTeam?.Players ?? [],
              LineUpInfo: awayTeam?.LineUpInfo ?? []
            }
          } as GameDetailsViewModel;

          if (!this._gameData?.Game) {
            this.loadError = 'Game data is incomplete';
            this._gameData = null;
            return;
          }

          this.cdr.detectChanges();
          this.loadScoutsForGameFromSchedule();
        },
        error: () => {
          this._gameData = null;
          this.gameCalendarEvents = [];
          this.scoutsForGame = [];
          this.loadError = 'Failed to load game details';
        }
      });


  }

  private loadScoutsForGameFromSchedule() {
    if (!this._gameData?.Game?.GameDate) {
      this.gameCalendarEvents = [];
      this.scoutsForGame = [];
      return;
    }

    const gameDate = new Date(this._gameData.Game.GameDate);
    const year = gameDate.getFullYear();
    const month = gameDate.getMonth() + 1;

    this.scheduleService.getSchedule(year, month)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (schedule) => {
        const events = (schedule?.EventList ?? []).filter(
          e => e.GameId === this._gameId
        );
        this.gameCalendarEvents = events;
        this.scoutsForGame = this.buildScoutRows(events);
        this.cdr.detectChanges();
      },
      error: () => {
        this.gameCalendarEvents = [];
        this.scoutsForGame = [];
        this.cdr.detectChanges();
      }
    });
  }

  private buildScoutRows(events: ScheduleEvent[]): { userId: string; displayName: string; comment: string }[] {
    const byScout = new Map<string, { userId: string; displayName: string; comment: string }>();

    for (const event of events) {
      const userId = event.UserId || `${event.FirstName}|${event.LastName}|${event.Email}`;
      const lastName = (event.LastName ?? '').trim();
      const firstName = (event.FirstName ?? '').trim();
      const displayName = `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`.trim() || event.Email || 'Unknown scout';
      const comment = (event.Comment ?? '').trim();

      if (!byScout.has(userId)) {
        byScout.set(userId, { userId, displayName, comment });
        continue;
      }

      // Prefer a non-empty comment if one exists across this scout's events.
      const existing = byScout.get(userId)!;
      if (!existing.comment && comment) {
        existing.comment = comment;
      }
    }

    return Array.from(byScout.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  
  getIconPath(icon: string): string {

    icon= icon.toLowerCase();
    if (icon.startsWith('/')) {
      return `../assets/img${icon}`;
    } else {
      return `../assets/img/${icon}`;
    }
  }

  importSweStats(){
    if (!this._gameData?.Game?.TeamHomeId) {
      return;
    }

    this.isBusy = true;
    this._statsService.getGameStatsFromSweHockey(this._gameId, this._gameData.Game.TeamHomeId)
      .pipe ( 
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isBusy=false;
          this.cdr.detectChanges();
        }),
        catchError((error) => [] )
      )
      .subscribe(s=>{
        //console.log("SweHockey import done" + s);
        this.getGameData(this._gameId);
      })
   
  }

}
